import React, { useEffect, useRef } from 'react';
import Userback from '@userback/widget';
import { useAuth } from '../contexts/AuthContext';
import { useEvent } from '../contexts/EventContext';
import { useLocation } from 'react-router-dom';

const UserbackWidget: React.FC = () => {
  const { user, isAuthenticated, currentGroupSlug, availableGroups, getUserMainPerson } = useAuth();
  const { selectedEvent, isInitialized: eventContextInitialized } = useEvent();
  const location = useLocation();
  const userbackInstance = useRef<any>(null);

  useEffect(() => {
    // Only initialize once we have basic auth data
    if (!isAuthenticated && user === null) {
      // Still loading auth state, wait
      return;
    }

    // For authenticated users in group pages, wait for event context to be initialized
    if (isAuthenticated && currentGroupSlug && !eventContextInitialized) {
      // Event context is still loading, wait
      return;
    }

    const initializeUserback = async () => {
      try {
        // Get current group info
        const currentGroup = availableGroups.find(group => group.slug === currentGroupSlug);
        
        // Get user's main person name if available
        let personName = user?.email || 'Unknown User';
        if (isAuthenticated && currentGroupSlug) {
          try {
            const mainPerson = await getUserMainPerson();
            if (mainPerson?.name) {
              personName = mainPerson.name;
            }
          } catch {
            // Fallback to email if person name is not available
          }
        }
        
        // Prepare options with user data if authenticated
        const options = isAuthenticated && user ? {
          user_data: {
            id: user.id.toString(),
            info: {
              name: personName,
              email: user.email,
              group_slug: currentGroupSlug || 'no-group',
              group_name: currentGroup?.name || 'Unknown Group',
              event_id: selectedEvent?.id?.toString() || 'no-event',
              event_name: selectedEvent?.name || 'No Event Selected',
              event_date: selectedEvent?.eventDate || 'no-date',
              current_page: location.pathname,
              is_group_admin: currentGroup ? availableGroups.some(g => g.slug === currentGroupSlug && g.isAdmin) : false,
            }
          }
        } : {
          user_data: {
            id: 'anonymous',
            info: {
              name: 'Anonymous User',
              current_page: location.pathname,
              group_slug: 'anonymous',
            }
          }
        };

        // Destroy previous instance if exists
        if (userbackInstance.current?.destroy) {
          userbackInstance.current.destroy();
          userbackInstance.current = null;
        }

        // Initialize Userback
        userbackInstance.current = await Userback("A-H70lB175JvvBtva0W5Y83j4B3", options);
      } catch (error) {
        console.error('Failed to initialize Userback:', error);
      }
    };

    // Debounce initialization to avoid too many calls
    const timer = setTimeout(initializeUserback, 200);

    // Cleanup function
    return () => {
      clearTimeout(timer);
    };
  }, [user, isAuthenticated, currentGroupSlug, selectedEvent, availableGroups, location.pathname, eventContextInitialized, getUserMainPerson]);

  return null; // This component doesn't render anything
};

export default UserbackWidget;